"use client";

import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { Icon } from "./Icon";
import { useTranslations } from "next-intl";

type SpecialtyKey = "cardiology" | "orthopaedics" | "oncology" | "paediatrics" | "ophthalmology";

const SPECIALTIES = [
  { id: "cardiology", name: "Cardiology", icon: "heart", color: "#CC2D4A" },
  { id: "orthopaedics", name: "Orthopaedics", icon: "bone", color: "#7C3AED" },
  { id: "oncology", name: "Oncology", icon: "cell", color: "#1E7BD7" },
  { id: "paediatrics", name: "Paediatrics", icon: "child", color: "#F59E0B" },
  { id: "ophthalmology", name: "Ophthalmology", icon: "eye", color: "#0891B2" },
];

export function EmrHeroAnimation() {
  const t = useTranslations("specializedEmr.emrHero");
  const [activeSpecialty, setActiveSpecialty] = useState<SpecialtyKey>("cardiology");
  const [isHovered, setIsHovered] = useState(false);

  // Auto-cycle through specialties every 4 seconds, pausing on hover
  useEffect(() => {
    if (isHovered) return;

    const interval = setInterval(() => {
      setActiveSpecialty((current) => {
        const currentIndex = SPECIALTIES.findIndex(s => s.id === current);
        const nextIndex = (currentIndex + 1) % SPECIALTIES.length;
        return SPECIALTIES[nextIndex].id as SpecialtyKey;
      });
    }, 4000);

    return () => clearInterval(interval);
  }, [isHovered]);

  return (
    <div
      className="w-full flex justify-center items-center px-0 overflow-visible"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="emr-hero-zoom-wrapper">
        <div className="emr-hero-card-container relative w-full max-w-[380px] sm:max-w-[480px] md:max-w-[740px] shrink-0 mx-auto flex items-center justify-center mb-0 md:-mt-8 lg:-mt-[240px]">

          {/* Main Large Dashboard Card */}
          <div
            className="relative z-10 w-full h-auto bg-white rounded-lg border border-slate-200/60 flex flex-col ring-1 ring-slate-900/5"
          >
            {/* Top window bar */}
            <div className="relative z-50 h-10 border-b border-slate-200/60 flex items-center px-5 gap-3.5 bg-gradient-to-r from-slate-50 to-slate-100/50 backdrop-blur-md rounded-t-lg">
              <div className="flex gap-2 shrink-0">
                <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F56] shadow-sm border border-black/5" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#FFBD2E] shadow-sm border border-black/5" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#27C93F] shadow-sm border border-black/5" />
              </div>
              <div className="flex-1" />
              {/* Specialty Selector Buttons - 5 Specialty Tabs */}
              <div className="flex items-center gap-1 bg-slate-200/50 p-1 rounded-lg border border-slate-300/50 shadow-inner overflow-x-auto scrollbar-none max-w-full touch-pan-x">
                {SPECIALTIES.map(s => {
                  const isActive = activeSpecialty === s.id;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveSpecialty(s.id as SpecialtyKey);
                      }}
                      className={cn(
                        "px-2.5 py-1 flex items-center gap-1 rounded-md text-[10px] uppercase tracking-wider font-extrabold transition-all duration-300 whitespace-nowrap shrink-0",
                        isActive
                          ? "bg-white text-slate-800 shadow-sm border border-slate-200/80"
                          : "text-slate-500 hover:text-slate-700 hover:bg-slate-100/50 border border-transparent"
                      )}
                    >
                      <Icon name={s.icon as any} width={11} height={11} style={{ color: isActive ? s.color : "#94a3b8" }} />
                      {t(`specialtiesTabs.${s.id}`)}
                    </button>
                  );
                })}
              </div>
              <div className="flex-1" />
            </div>

            {/* Inner Content Layout - Responsive 1 or 2 Columns */}
            <div className="flex-1 p-3 md:p-5 flex flex-col md:flex-row gap-4 md:gap-5 bg-[#FAFAFA] relative overflow-hidden rounded-b-lg">
              {/* subtle noise/pattern */}
              <div className="absolute inset-0 opacity-[0.03] bg-[radial-gradient(#000_1px,transparent_1px)] [background-size:16px_16px]" />

              {/* LEFT COLUMN: Main specialty dashboard content */}
              <div className="flex-1 flex flex-col gap-3 md:gap-4">

                {activeSpecialty === "cardiology" && (
                  <>
                    <div className="grid grid-cols-3 gap-1.5 sm:gap-3 md:gap-4 relative z-10">
                {/* Heart Rate EKG */}
                <div className="h-auto md:h-20 bg-white rounded-lg border border-slate-200/60 shadow-sm p-1.5 md:p-3 flex flex-col justify-center md:justify-between hover:shadow-md transition-all group relative overflow-hidden">
                  <div className="flex flex-col md:flex-row items-center md:justify-between relative z-10 gap-0.5 md:gap-0">
                    <div className="flex items-center gap-1 md:gap-1.5 text-rose-500">
                      <Icon name="heart" width={14} height={14} className="w-3 h-3 md:w-3.5 md:h-3.5" />
                      <span className="text-[7.5px] md:text-[10px] font-extrabold uppercase tracking-widest text-slate-400">{t("heartRate")}</span>
                    </div>
                    <span className="text-[10px] md:text-sm font-black text-slate-900">{t("hrValue")} <span className="text-[7px] md:text-[9px] text-slate-400 font-bold">{t("bpm")}</span></span>
                  </div>
                  <div className="hidden md:block h-6 w-full mt-1 relative z-10 overflow-hidden">
                    <svg viewBox="0 0 100 24" preserveAspectRatio="none" className="w-full h-full">
                      <motion.path
                        d="M 0 12 L 10 12 L 15 4 L 20 20 L 25 12 L 50 12 L 55 4 L 60 20 L 65 12 L 100 12"
                        fill="none" stroke="#CC2D4A" strokeWidth="2" strokeLinejoin="round"
                        initial={{ pathLength: 0, opacity: 0 }} animate={{ pathLength: 1, opacity: 1 }}
                        transition={{ pathLength: { duration: 1.5, repeat: Infinity, ease: "linear" }, opacity: { duration: 0.1 } }}
                      />
                    </svg>
                  </div>
                </div>

                {/* Blood Pressure Chart */}
                <div className="h-auto md:h-20 bg-white rounded-lg border border-slate-200/60 shadow-sm p-1.5 md:p-3 flex flex-col justify-center md:justify-between hover:shadow-md transition-all relative overflow-hidden">
                  <div className="flex flex-col md:flex-row items-center md:justify-between relative z-10 gap-0.5 md:gap-0">
                    <div className="flex items-center gap-1 md:gap-1.5 text-blue-500">
                      <Icon name="chart" width={14} height={14} className="w-3 h-3 md:w-3.5 md:h-3.5" />
                      <span className="text-[7.5px] md:text-[10px] font-extrabold uppercase tracking-widest text-slate-400">{t("bpHist")}</span>
                    </div>
                    <span className="text-[10px] md:text-sm font-black text-slate-900">{t("bpValue")}</span>
                  </div>
                  <div className="hidden md:flex items-end justify-between gap-[3px] h-7 mt-1.5 w-full relative z-10">
                    {[40, 75, 55, 95, 80, 60].map((h, i) => (
                      <motion.div key={i} className="w-full rounded-t-sm bg-blue-50" style={{ height: `${h}%`, transformOrigin: "bottom" }}
                        initial={{ scaleY: 0 }} animate={{ scaleY: [0, 1.1, 1] }}
                        transition={{ duration: 0.5, delay: i * 0.1, repeat: Infinity, repeatDelay: 2, ease: "easeOut" }}
                      >
                        <div className="w-full h-full bg-blue-500 rounded-t-sm opacity-60 hover:opacity-100 transition-opacity cursor-pointer" />
                      </motion.div>
                    ))}
                  </div>
                  <div className="hidden md:block absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-blue-50/50 to-transparent pointer-events-none" />
                </div>

                {/* O2 Saturation Pulse */}
                <div className="h-auto md:h-20 bg-white rounded-lg border border-slate-200/60 shadow-sm p-1.5 md:p-3 flex flex-col justify-center md:justify-between hover:shadow-md transition-all overflow-hidden relative group">
                  <div className="flex flex-col md:flex-row items-center md:justify-between relative z-10 gap-0.5 md:gap-0">
                    <div className="flex items-center gap-1 md:gap-1.5 text-emerald-500">
                      <Icon name="droplet" width={14} height={14} className="w-3 h-3 md:w-3.5 md:h-3.5" />
                      <span className="text-[7.5px] md:text-[10px] font-extrabold uppercase tracking-widest text-slate-400">{t("spo2")}</span>
                    </div>
                    <span className="text-[10px] md:text-sm font-black text-slate-900">{t("spo2Value")}</span>
                  </div>
                  <div className="hidden md:flex absolute -bottom-5 -right-4 w-24 h-24 bg-emerald-50/50 rounded-full items-center justify-center group-hover:scale-110 transition-transform duration-500 z-0">
                    <motion.div className="absolute inset-0 rounded-full border-2 border-emerald-400/20"
                      initial={{ scale: 0.5, opacity: 1 }} animate={{ scale: 2, opacity: 0 }}
                      transition={{ duration: 2, repeat: Infinity, ease: "easeOut" }} />
                    <motion.div className="absolute inset-0 rounded-full border-2 border-emerald-400/20"
                      initial={{ scale: 0.5, opacity: 1 }} animate={{ scale: 2, opacity: 0 }}
                      transition={{ duration: 2, delay: 1, repeat: Infinity, ease: "easeOut" }} />
                    <div className="w-8 h-8 bg-emerald-100/80 rounded-full" />
                  </div>
                </div>
              </div>
                  </>
                )}

                {/* 2. Orthopaedics KPIs */}
                {activeSpecialty === "orthopaedics" && (
                  <>
                    <div className="grid grid-cols-3 gap-1.5 sm:gap-3 md:gap-4 relative z-10">
                {/* Pain Scale */}
                <div className="h-auto md:h-20 bg-white rounded-lg border border-slate-200/60 shadow-sm p-1.5 md:p-3 flex flex-col justify-center md:justify-between hover:shadow-md transition-all group relative overflow-hidden">
                  <div className="flex flex-col md:flex-row items-center md:justify-between relative z-10 gap-0.5 md:gap-0">
                    <div className="flex items-center gap-1 md:gap-1.5 text-purple-500">
                      <Icon name="activity" width={14} height={14} className="w-3 h-3 md:w-3.5 md:h-3.5" />
                      <span className="text-[7.5px] md:text-[10px] font-extrabold uppercase tracking-widest text-slate-400">{t("orthopaedics.painScale")}</span>
                    </div>
                    <span className="text-[10px] md:text-sm font-black text-slate-900">4/10</span>
                  </div>
                  <div className="hidden md:flex items-center gap-[3px] h-5 mt-2 w-full relative z-10">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((v, i) => (
                      <motion.div key={v} className={`flex-1 h-full rounded-sm ${v <= 4 ? 'bg-purple-500' : 'bg-slate-100'}`}
                        initial={{ scaleY: 0 }} animate={{ scaleY: 1 }} style={{ transformOrigin: 'bottom' }} transition={{ delay: i * 0.05 }}
                      />
                    ))}
                  </div>
                </div>

                {/* Range of Motion */}
                <div className="h-auto md:h-20 bg-white rounded-lg border border-slate-200/60 shadow-sm p-1.5 md:p-3 flex flex-col justify-center md:justify-between hover:shadow-md transition-all group relative overflow-hidden">
                  <div className="flex flex-col md:flex-row items-center md:justify-between relative z-10 gap-0.5 md:gap-0">
                    <div className="flex items-center gap-1 md:gap-1.5 text-blue-500">
                      <Icon name="activity" width={14} height={14} className="w-3 h-3 md:w-3.5 md:h-3.5" />
                      <span className="text-[7.5px] md:text-[10px] font-extrabold uppercase tracking-widest text-slate-400">{t("orthopaedics.romKnee")}</span>
                    </div>
                    <span className="text-[10px] md:text-sm font-black text-slate-900">110°</span>
                  </div>
                  <div className="hidden md:flex h-8 w-full mt-1 items-end justify-center relative z-10 overflow-hidden">
                    <motion.div className="w-16 h-16 rounded-full border-[5px] border-slate-100 border-t-blue-500 border-r-blue-500 translate-y-4"
                      initial={{ rotate: -90 }} animate={{ rotate: -45 }} transition={{ duration: 1, type: "spring" }}
                    />
                  </div>
                </div>

                {/* Mobility */}
                <div className="h-auto md:h-20 bg-white rounded-lg border border-slate-200/60 shadow-sm p-1.5 md:p-3 flex flex-col justify-center md:justify-between hover:shadow-md transition-all group relative overflow-hidden">
                  <div className="flex flex-col md:flex-row items-center md:justify-between relative z-10 gap-0.5 md:gap-0">
                    <div className="flex items-center gap-1 md:gap-1.5 text-emerald-500">
                      <Icon name="user" width={14} height={14} className="w-3 h-3 md:w-3.5 md:h-3.5" />
                      <span className="text-[7.5px] md:text-[10px] font-extrabold uppercase tracking-widest text-slate-400">{t("orthopaedics.mobility")}</span>
                    </div>
                    <span className="md:hidden text-[7.5px] font-bold text-slate-600 text-center leading-tight">{t("orthopaedics.wbat")}</span>
                  </div>
                  <div className="hidden md:flex items-center mt-1 relative z-10">
                    <span className="text-[10px] font-bold text-slate-600 leading-tight">{t("orthopaedics.wbatFull")}</span>
                  </div>
                </div>
              </div>
                  </>
                )}

                {/* 3. Oncology KPIs */}
                {activeSpecialty === "oncology" && (
                  <>
                    <div className="grid grid-cols-3 gap-1.5 sm:gap-3 md:gap-4 relative z-10">
                {/* Tumor Marker */}
                <div className="h-auto md:h-20 bg-white rounded-lg border border-slate-200/60 shadow-sm p-1.5 md:p-3 flex flex-col justify-center md:justify-between hover:shadow-md transition-all group relative overflow-hidden">
                  <div className="flex flex-col md:flex-row items-center md:justify-between relative z-10 gap-0.5 md:gap-0">
                    <div className="flex items-center gap-1 md:gap-1.5 text-blue-500">
                      <Icon name="activity" width={14} height={14} className="w-3 h-3 md:w-3.5 md:h-3.5" />
                      <span className="text-[7.5px] md:text-[10px] font-extrabold uppercase tracking-widest text-slate-400">{t("oncology.ceaLevel")}</span>
                    </div>
                    <span className="text-[10px] md:text-sm font-black text-slate-900">2.4 <span className="text-[7px] md:text-[9px] text-slate-400 font-bold">ng/mL</span></span>
                  </div>
                  <div className="hidden md:flex h-7 w-full mt-1.5 relative z-10 items-end gap-[4px]">
                    {[4.2, 3.8, 3.1, 2.7, 2.4].map((v, i) => (
                      <motion.div key={i} className="flex-1 bg-blue-500/80 rounded-t-sm" style={{ height: `${(v / 5) * 100}%`, transformOrigin: 'bottom' }}
                        initial={{ scaleY: 0 }} animate={{ scaleY: 1 }} transition={{ delay: i * 0.1 }}
                      />
                    ))}
                  </div>
                </div>

                {/* Chemo Cycle */}
                <div className="h-auto md:h-20 bg-white rounded-lg border border-slate-200/60 shadow-sm p-1.5 md:p-3 flex flex-col justify-center md:justify-between hover:shadow-md transition-all group relative overflow-hidden">
                  <div className="flex flex-col md:flex-row items-center md:justify-between relative z-10 gap-0.5 md:gap-0">
                    <div className="flex items-center gap-1 md:gap-1.5 text-rose-500">
                      <Icon name="pill" width={14} height={14} className="w-3 h-3 md:w-3.5 md:h-3.5" />
                      <span className="text-[7.5px] md:text-[10px] font-extrabold uppercase tracking-widest text-slate-400">{t("oncology.cycle")}</span>
                    </div>
                    <span className="text-[10px] md:text-sm font-black text-slate-900">3 of 6</span>
                  </div>
                  <div className="hidden md:flex items-center gap-1.5 mt-2 w-full relative z-10">
                    {[1, 2, 3, 4, 5, 6].map((v, i) => (
                      <motion.div key={v} className={`w-3.5 h-3.5 rounded-full ${v <= 3 ? 'bg-rose-500' : 'bg-slate-200'}`}
                        initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: i * 0.05 }}
                      />
                    ))}
                  </div>
                </div>

                {/* ANC Count */}
                <div className="h-auto md:h-20 bg-white rounded-lg border border-slate-200/60 shadow-sm p-1.5 md:p-3 flex flex-col justify-center md:justify-between hover:shadow-md transition-all group relative overflow-hidden">
                  <div className="flex flex-col md:flex-row items-center md:justify-between relative z-10 gap-0.5 md:gap-0">
                    <div className="flex items-center gap-1 md:gap-1.5 text-emerald-500">
                      <Icon name="droplet" width={14} height={14} className="w-3 h-3 md:w-3.5 md:h-3.5" />
                      <span className="text-[7.5px] md:text-[10px] font-extrabold uppercase tracking-widest text-slate-400">{t("oncology.anc")}</span>
                    </div>
                    <span className="text-[10px] md:text-sm font-black text-slate-900">1800</span>
                  </div>
                  <div className="hidden md:flex items-center mt-1 relative z-10">
                    <span className="text-[9px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-100">{t("oncology.normalRange")}</span>
                  </div>
                </div>
              </div>
                  </>
                )}

                {/* 4. Paediatrics KPIs */}
                {activeSpecialty === "paediatrics" && (
                  <>
                    <div className="grid grid-cols-3 gap-1.5 sm:gap-3 md:gap-4 relative z-10">
                {/* Growth Curve */}
                <div className="h-auto md:h-20 bg-white rounded-lg border border-slate-200/60 shadow-sm p-1.5 md:p-2.5 flex flex-col justify-center md:justify-between hover:shadow-md transition-all relative overflow-hidden">
                  <div className="flex flex-col md:flex-row items-center md:justify-between gap-0.5 md:gap-1 relative z-10">
                    <div className="flex items-center gap-0.5 md:gap-1 text-amber-500 min-w-0">
                      <Icon name="child" width={13} height={13} className="shrink-0 w-3 h-3 md:w-[13px] md:h-[13px]" />
                      <span className="text-[7.5px] md:text-[9px] font-extrabold uppercase tracking-wider text-slate-400 truncate">{t("paediatrics.growth")}</span>
                    </div>
                    <span className="text-[10px] md:text-xs font-black text-slate-900 shrink-0 text-center">98th <span className="text-[7px] md:text-[8px] text-slate-400 font-bold">{t("paediatrics.percentile")}</span></span>
                  </div>
                  <div className="hidden md:flex h-5 w-full relative z-10 items-end gap-[3px]">
                    {[45, 60, 75, 88, 98].map((v, i) => (
                      <motion.div key={i} className="flex-1 bg-amber-500/80 rounded-t-sm" style={{ height: `${v}%`, transformOrigin: 'bottom' }}
                        initial={{ scaleY: 0 }} animate={{ scaleY: 1 }} transition={{ delay: i * 0.08 }}
                      />
                    ))}
                  </div>
                </div>

                {/* Vaccines */}
                <div className="h-auto md:h-20 bg-white rounded-lg border border-slate-200/60 shadow-sm p-1.5 md:p-2.5 flex flex-col justify-center md:justify-between hover:shadow-md transition-all relative overflow-hidden">
                  <div className="flex flex-col md:flex-row items-center md:justify-between gap-0.5 md:gap-1 relative z-10">
                    <div className="flex items-center gap-0.5 md:gap-1 text-sky-500 min-w-0">
                      <Icon name="droplets" width={13} height={13} className="shrink-0 w-3 h-3 md:w-[13px] md:h-[13px]" />
                      <span className="text-[7.5px] md:text-[9px] font-extrabold uppercase tracking-wider text-slate-400 truncate">{t("paediatrics.vaccines")}</span>
                    </div>
                    <span className="text-[7.5px] md:text-[9px] font-black text-emerald-600 bg-emerald-50 px-1 md:px-1.5 py-0.5 rounded border border-emerald-100/60 shrink-0 text-center">{t("paediatrics.vaccinesDone")}</span>
                  </div>
                  <div className="hidden md:flex items-center justify-between gap-1 w-full relative z-10 pb-0.5">
                    {["MMR", "DTP", "HepB", "Polio"].map((v) => (
                      <div key={v} className="flex-1 flex flex-col items-center gap-0.5">
                        <div className="w-1.5 h-1.5 rounded-full bg-sky-500" />
                        <span className="text-[7px] font-extrabold text-slate-500 leading-none">{v}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Milestones */}
                <div className="h-auto md:h-20 bg-white rounded-lg border border-slate-200/60 shadow-sm p-1.5 md:p-2.5 flex flex-col justify-center md:justify-between hover:shadow-md transition-all relative overflow-hidden">
                  <div className="flex flex-col md:flex-row items-center md:justify-between gap-0.5 md:gap-1 relative z-10">
                    <div className="flex items-center gap-0.5 md:gap-1 text-emerald-500 min-w-0">
                      <Icon name="star" width={13} height={13} className="shrink-0 w-3 h-3 md:w-[13px] md:h-[13px]" />
                      <span className="text-[7.5px] md:text-[9px] font-extrabold uppercase tracking-wider text-slate-400 truncate">{t("paediatrics.milestone")}</span>
                    </div>
                    <span className="text-[10px] md:text-xs font-black text-slate-900 shrink-0 text-center">24m</span>
                  </div>
                  <div className="hidden md:flex items-center relative z-10 pb-0.5">
                    <span className="text-[8.5px] font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-md border border-emerald-100/60 truncate w-full text-center">
                      {t("paediatrics.motorSpeechOk")}
                    </span>
                  </div>
                </div>
              </div>
                  </>
                )}

                {/* 5. Ophthalmology KPIs */}
                {activeSpecialty === "ophthalmology" && (
                  <>
                    <div className="grid grid-cols-3 gap-1.5 sm:gap-3 md:gap-4 relative z-10">
                {/* Visual Acuity */}
                <div className="h-auto md:h-20 bg-white rounded-lg border border-slate-200/60 shadow-sm p-1.5 md:p-2.5 flex flex-col justify-center md:justify-between hover:shadow-md transition-all relative overflow-hidden">
                  <div className="flex flex-col md:flex-row items-center md:justify-between gap-0.5 md:gap-1 relative z-10">
                    <div className="flex items-center gap-0.5 md:gap-1 text-cyan-600 min-w-0">
                      <Icon name="eye" width={13} height={13} className="shrink-0 w-3 h-3 md:w-[13px] md:h-[13px]" />
                      <span className="text-[7.5px] md:text-[9px] font-extrabold uppercase tracking-wider text-slate-400 truncate">{t("ophthalmology.acuity")}</span>
                    </div>
                    <span className="text-[10px] md:text-xs font-black text-slate-900 shrink-0 text-center">20/20</span>
                  </div>
                  <div className="hidden md:flex items-center justify-between gap-1 relative z-10 pb-0.5">
                    <span className="text-[8px] font-extrabold text-cyan-700 bg-cyan-50/80 px-1 py-0.5 rounded border border-cyan-100 truncate">{t("ophthalmology.od2020")}</span>
                    <span className="text-[8px] font-extrabold text-slate-600 bg-slate-100 px-1 py-0.5 rounded border border-slate-200 truncate">{t("ophthalmology.os2025")}</span>
                  </div>
                </div>

                {/* IOP Pressure */}
                <div className="h-auto md:h-20 bg-white rounded-lg border border-slate-200/60 shadow-sm p-1.5 md:p-2.5 flex flex-col justify-center md:justify-between hover:shadow-md transition-all relative overflow-hidden">
                  <div className="flex flex-col md:flex-row items-center md:justify-between gap-0.5 md:gap-1 relative z-10">
                    <div className="flex items-center gap-0.5 md:gap-1 text-emerald-500 min-w-0">
                      <Icon name="activity" width={13} height={13} className="shrink-0 w-3 h-3 md:w-[13px] md:h-[13px]" />
                      <span className="text-[7.5px] md:text-[9px] font-extrabold uppercase tracking-wider text-slate-400 truncate">{t("ophthalmology.iop")}</span>
                    </div>
                    <span className="text-[10px] md:text-xs font-black text-slate-900 shrink-0 text-center">14 <span className="text-[7px] md:text-[8px] text-slate-400 font-bold">mmHg</span></span>
                  </div>
                  <div className="hidden md:flex items-end justify-between gap-1.5 h-5 w-full relative z-10">
                    <motion.div className="flex-1 bg-emerald-400 rounded-t-sm h-[70%]" initial={{ scaleY: 0 }} animate={{ scaleY: 1 }} style={{ transformOrigin: "bottom" }} transition={{ duration: 0.6 }} />
                    <motion.div className="flex-1 bg-emerald-500 rounded-t-sm h-[75%]" initial={{ scaleY: 0 }} animate={{ scaleY: 1 }} style={{ transformOrigin: "bottom" }} transition={{ duration: 0.6, delay: 0.1 }} />
                  </div>
                </div>

                {/* Refraction */}
                <div className="h-auto md:h-20 bg-white rounded-lg border border-slate-200/60 shadow-sm p-1.5 md:p-2.5 flex flex-col justify-center md:justify-between hover:shadow-md transition-all relative overflow-hidden">
                  <div className="flex flex-col md:flex-row items-center md:justify-between gap-0.5 md:gap-1 relative z-10">
                    <div className="flex items-center gap-0.5 md:gap-1 text-purple-500 min-w-0">
                      <Icon name="sparkle" width={13} height={13} className="shrink-0 w-3 h-3 md:w-[13px] md:h-[13px]" />
                      <span className="text-[7.5px] md:text-[9px] font-extrabold uppercase tracking-wider text-slate-400 truncate">{t("ophthalmology.refraction")}</span>
                    </div>
                    <span className="text-[10px] md:text-xs font-black text-slate-900 shrink-0 text-center">-1.50 D</span>
                  </div>
                  <div className="hidden md:flex items-center relative z-10 pb-0.5">
                    <span className="text-[8.5px] font-extrabold text-purple-700 bg-purple-50 px-1.5 py-0.5 rounded-md border border-purple-100/60 truncate w-full text-center">
                      {t("ophthalmology.bilateralCorrection")}
                    </span>
                  </div>
                </div>
              </div>
                  </>
                )}

                {/* Contextual Analysis Section — Fixed identical height h-[230px] across all 5 specialties */}
                {activeSpecialty === "cardiology" && (
                  <div className="h-[230px] bg-white rounded-lg border border-slate-200/60 shadow-sm p-4 flex flex-col justify-between relative z-10 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-rose-50 rounded-lg text-rose-500 border border-rose-100/50">
                          <Icon name="activity" width={13} height={13} />
                        </div>
                        <span className="text-xs font-bold text-slate-700">{t("contextualAnalysis.cardiology.ecgEcho")}</span>
                      </div>
                      <span className="text-[9px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full flex items-center gap-1 border border-emerald-100"><span className="w-1 h-1 bg-emerald-500 rounded-full animate-pulse" /> {t("contextualAnalysis.cardiology.live")}</span>
                    </div>
                    {/* EKG strip */}
                    <div className="h-7 bg-slate-50 rounded-lg border border-slate-100 relative overflow-hidden flex items-center">
                      <svg viewBox="0 0 200 30" preserveAspectRatio="none" className="w-full h-full relative z-10 opacity-60">
                        <path d="M 0 15 L 25 15 L 30 5 L 35 25 L 40 15 L 80 15 L 85 5 L 90 25 L 95 15 L 140 15 L 145 5 L 150 25 L 155 15 L 200 15" fill="none" stroke="#e11d48" strokeWidth="1.2" strokeLinejoin="round" />
                      </svg>
                      <motion.div className="absolute w-2 h-full bg-gradient-to-r from-transparent via-rose-400/40 to-transparent" animate={{ left: ["-5%", "105%"] }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }} />
                    </div>
                    {/* Finding rows */}
                    <div className="flex flex-col gap-1">
                      {[
                        { label: t("contextualAnalysis.cardiology.sinusRhythm"), value: t("contextualAnalysis.cardiology.normal"), ok: true },
                        { label: t("contextualAnalysis.cardiology.ischemicChanges"), value: t("contextualAnalysis.cardiology.none"), ok: true },
                        { label: t("contextualAnalysis.cardiology.lvef"), value: "62%", ok: true },
                        { label: t("contextualAnalysis.cardiology.prInterval"), value: "164 ms", ok: true },
                      ].map((row, i) => (
                        <motion.div key={i} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.1 }}
                          className="flex justify-between items-center bg-slate-50/60 px-2.5 py-1 rounded-lg border border-slate-100 text-[10px]">
                          <span className="text-slate-500 font-medium">{row.label}</span>
                          <span className={cn("font-bold", row.ok ? "text-emerald-600" : "text-rose-600")}>{row.value}</span>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}

                {activeSpecialty === "orthopaedics" && (
                  <div className="h-[230px] bg-white rounded-lg border border-slate-200/60 shadow-sm p-4 flex flex-col justify-between relative z-10 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-blue-50 rounded-lg text-blue-500 border border-blue-100/50">
                          <Icon name="bone" width={13} height={13} />
                        </div>
                        <span className="text-xs font-bold text-slate-700">{t("contextualAnalysis.orthopaedics.romAssessment")}</span>
                      </div>
                      <span className="text-[9px] font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full flex items-center gap-1 border border-blue-100"><span className="w-1 h-1 bg-blue-500 rounded-full animate-pulse" /> {t("contextualAnalysis.orthopaedics.tracking")}</span>
                    </div>
                    {/* ROM progress bars */}
                    <div className="flex flex-col gap-2 my-auto">
                      {[
                        { label: t("contextualAnalysis.orthopaedics.flexion"), target: "120°", value: 85, color: "bg-blue-500" },
                        { label: t("contextualAnalysis.orthopaedics.extension"), target: "0°", value: 40, color: "bg-slate-400" },
                        { label: t("contextualAnalysis.orthopaedics.intRotation"), target: "45°", value: 70, color: "bg-purple-400" },
                        { label: t("contextualAnalysis.orthopaedics.extRotation"), target: "45°", value: 60, color: "bg-emerald-500" },
                      ].map((r, i) => (
                        <motion.div key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.1 }} className="flex flex-col gap-1">
                          <div className="flex justify-between text-[9px]">
                            <span className="text-slate-500 font-medium">{r.label} <span className="text-slate-400">({t("contextualAnalysis.orthopaedics.tgt")} {r.target})</span></span>
                            <span className="font-bold text-slate-700">{r.value}%</span>
                          </div>
                          <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden">
                            <motion.div className={cn("h-full rounded-full", r.color)} initial={{ width: 0 }} animate={{ width: `${r.value}%` }} transition={{ duration: 1, delay: i * 0.15 }} />
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}

                {activeSpecialty === "oncology" && (
                  <div className="h-[230px] bg-white rounded-lg border border-slate-200/60 shadow-sm p-4 flex flex-col justify-between relative z-10 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-purple-50 rounded-lg text-purple-500 border border-purple-100/50">
                          <Icon name="ai" width={13} height={13} />
                        </div>
                        <span className="text-xs font-bold text-slate-700">{t("contextualAnalysis.oncology.treatmentResponse")}</span>
                      </div>
                      <span className="text-[9px] font-bold text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full flex items-center gap-1 border border-purple-100"><span className="w-1 h-1 bg-purple-500 rounded-full animate-pulse" /> {t("contextualAnalysis.oncology.monitoring")}</span>
                    </div>
                    {/* Tumour regression bars */}
                    <div className="flex gap-2 items-end h-[48px] w-full px-1 my-1">
                      {[{ m: t("contextualAnalysis.oncology.baseline"), v: 100, c: "bg-purple-200" }, { m: t("contextualAnalysis.oncology.month1"), v: 72, c: "bg-purple-300" }, { m: t("contextualAnalysis.oncology.month2"), v: 41, c: "bg-purple-400" }, { m: t("contextualAnalysis.oncology.estM3"), v: 15, c: "bg-purple-100 border border-purple-300 border-dashed" }].map((d, i) => (
                        <div key={i} className="flex-1 flex flex-col items-center gap-1">
                          <div className="w-full h-9 bg-slate-50 rounded-sm relative flex items-end border-b border-slate-200 overflow-hidden">
                            <motion.div className={cn("w-full rounded-t-sm", d.c)} style={{ height: 0 }} animate={{ height: `${d.v}%` }} transition={{ duration: 1.2, delay: i * 0.15, type: "spring", bounce: 0.2 }} />
                          </div>
                          <span className="text-[8px] font-bold text-slate-400 text-center leading-none">{d.m}</span>
                        </div>
                      ))}
                    </div>
                    {/* Adverse event rows */}
                    <div className="flex flex-col gap-1">
                      {[
                        { label: t("contextualAnalysis.oncology.fatigue"), value: t("contextualAnalysis.oncology.mild"), level: "low" },
                        { label: t("contextualAnalysis.oncology.nausea"), value: t("contextualAnalysis.oncology.none"), level: "ok" },
                        { label: t("contextualAnalysis.oncology.neuropathy"), value: t("contextualAnalysis.oncology.mild"), level: "low" },
                      ].map((row, i) => (
                        <motion.div key={i} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.6 + i * 0.1 }}
                          className="flex justify-between items-center bg-slate-50/60 px-2.5 py-1 rounded-lg border border-slate-100 text-[10px]">
                          <span className="text-slate-500 font-medium">{row.label}</span>
                          <span className={cn("font-bold px-1.5 py-0.5 rounded text-[9px]", row.level === "ok" ? "text-emerald-600 bg-emerald-50" : "text-amber-600 bg-amber-50")}>{row.value}</span>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}

                {activeSpecialty === "paediatrics" && (
                  <div className="h-[230px] bg-white rounded-lg border border-slate-200/60 shadow-sm p-4 flex flex-col justify-between relative z-10 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-amber-50 rounded-lg text-amber-500 border border-amber-100/50">
                          <Icon name="child" width={13} height={13} />
                        </div>
                        <span className="text-xs font-bold text-slate-700">{t("contextualAnalysis.paediatrics.whoGrowth")}</span>
                      </div>
                      <span className="text-[9px] font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full flex items-center gap-1 border border-amber-100"><span className="w-1 h-1 bg-amber-500 rounded-full animate-pulse" /> {t("contextualAnalysis.paediatrics.liveSync")}</span>
                    </div>
                    {/* Growth curve bars */}
                    <div className="flex gap-2 items-end h-[48px] w-full px-1 my-1">
                      {[{ m: t("contextualAnalysis.paediatrics.m6"), v: 45, c: "bg-amber-200" }, { m: t("contextualAnalysis.paediatrics.m12"), v: 62, c: "bg-amber-300" }, { m: t("contextualAnalysis.paediatrics.m18"), v: 80, c: "bg-amber-400" }, { m: t("contextualAnalysis.paediatrics.m24"), v: 98, c: "bg-amber-500" }].map((d, i) => (
                        <div key={i} className="flex-1 flex flex-col items-center gap-1">
                          <div className="w-full h-9 bg-slate-50 rounded-sm relative flex items-end border-b border-slate-200 overflow-hidden">
                            <motion.div className={cn("w-full rounded-t-sm", d.c)} style={{ height: 0 }} animate={{ height: `${d.v}%` }} transition={{ duration: 1.2, delay: i * 0.15, type: "spring", bounce: 0.2 }} />
                          </div>
                          <span className="text-[8px] font-bold text-slate-400 text-center leading-none">{d.m}</span>
                        </div>
                      ))}
                    </div>
                    {/* Paediatric observation rows */}
                    <div className="flex flex-col gap-1">
                      {[
                        { label: t("contextualAnalysis.paediatrics.weightForAge"), value: t("contextualAnalysis.paediatrics.weightVal"), ok: true },
                        { label: t("contextualAnalysis.paediatrics.heightForAge"), value: t("contextualAnalysis.paediatrics.heightVal"), ok: true },
                        { label: t("contextualAnalysis.paediatrics.headCircum"), value: t("contextualAnalysis.paediatrics.headVal"), ok: true },
                        { label: t("contextualAnalysis.paediatrics.vaxStatus"), value: t("contextualAnalysis.paediatrics.vaxVal"), ok: true },
                      ].map((row, i) => (
                        <motion.div key={i} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.1 }}
                          className="flex justify-between items-center bg-slate-50/60 px-2.5 py-1 rounded-lg border border-slate-100 text-[10px]">
                          <span className="text-slate-500 font-medium truncate w-[60%]">{row.label}</span>
                          <span className="font-bold text-emerald-600 truncate">{row.value}</span>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}

                {activeSpecialty === "ophthalmology" && (
                  <div className="h-[230px] bg-white rounded-lg border border-slate-200/60 shadow-sm p-4 flex flex-col justify-between relative z-10 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-cyan-50 rounded-lg text-cyan-600 border border-cyan-100/50">
                          <Icon name="eye" width={13} height={13} />
                        </div>
                        <span className="text-xs font-bold text-slate-700">{t("contextualAnalysis.ophthalmology.acuityGrid")}</span>
                      </div>
                      <span className="text-[9px] font-bold text-cyan-600 bg-cyan-50 px-2 py-0.5 rounded-full flex items-center gap-1 border border-cyan-100"><span className="w-1 h-1 bg-cyan-500 rounded-full animate-pulse" /> {t("contextualAnalysis.ophthalmology.exam")}</span>
                    </div>
                    {/* Visual acuity side-by-side comparison */}
                    <div className="grid grid-cols-2 gap-2 my-auto">
                      <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
                        className="p-2.5 rounded-xl bg-cyan-50/40 border border-cyan-100 flex flex-col gap-1"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] font-black text-cyan-800">{t("contextualAnalysis.ophthalmology.rightEye")}</span>
                          <span className="text-[9px] font-extrabold text-cyan-600">{t("contextualAnalysis.ophthalmology.rightVal")}</span>
                        </div>
                        <span className="text-[9px] font-medium text-slate-500">{t("contextualAnalysis.ophthalmology.rightDesc")}</span>
                      </motion.div>

                      <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
                        className="p-2.5 rounded-xl bg-slate-50 border border-slate-200/80 flex flex-col gap-1"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] font-black text-slate-700">{t("contextualAnalysis.ophthalmology.leftEye")}</span>
                          <span className="text-[9px] font-extrabold text-slate-600">{t("contextualAnalysis.ophthalmology.leftVal")}</span>
                        </div>
                        <span className="text-[9px] font-medium text-slate-500">{t("contextualAnalysis.ophthalmology.leftDesc")}</span>
                      </motion.div>
                    </div>
                    {/* Exam rows */}
                    <div className="flex flex-col gap-1">
                      {[
                        { label: t("contextualAnalysis.ophthalmology.refractionSph"), value: t("contextualAnalysis.ophthalmology.refractVal"), ok: true },
                        { label: t("contextualAnalysis.ophthalmology.fundusRetina"), value: t("contextualAnalysis.ophthalmology.fundusVal"), ok: true },
                      ].map((row, i) => (
                        <motion.div key={i} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 + i * 0.1 }}
                          className="flex justify-between items-center bg-slate-50/60 px-2.5 py-1 rounded-lg border border-slate-100 text-[10px]">
                          <span className="text-slate-500 font-medium truncate w-[60%]">{row.label}</span>
                          <span className="font-bold text-slate-700 truncate">{row.value}</span>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* RIGHT COLUMN: Micro-components side panel - Responsive Stack */}
              <div className="flex flex-col gap-3 md:gap-4 w-full md:w-[240px] border-t md:border-t-0 md:border-l border-slate-200/60 pt-4 md:pt-0 pl-0 md:pl-5 relative z-10 shrink-0">
                {activeSpecialty === "cardiology" && (
                  <>
                    <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">{t("micro.cardiology.monitoring")}</h4>

                    <div className="h-[145px] bg-white rounded-lg border border-slate-200/60 p-3 flex flex-col justify-between shadow-sm hover:shadow-md transition-shadow">
                      <div className="flex items-center gap-2 mb-1">
                        <Icon name="pill" width={13} height={13} className="text-indigo-500" />
                        <span className="text-[11px] font-bold text-slate-700">{t("micro.cardiology.medication")}</span>
                      </div>
                      <div className="flex flex-col gap-1.5">
                        {[
                          { name: "Metoprolol", dose: "50mg", freq: "BD" },
                          { name: "Aspirin", dose: "81mg", freq: "OD" },
                          { name: "Atorvastatin", dose: "40mg", freq: "HS" },
                        ].map((med, i) => (
                          <motion.div key={i} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
                            className="flex items-center justify-between bg-indigo-50/40 border border-indigo-100/50 rounded-lg px-2 py-1">
                            <span className="text-[10px] font-semibold text-slate-700">{med.name}</span>
                            <span className="text-[9px] text-slate-400 font-bold">{med.dose} · {med.freq}</span>
                          </motion.div>
                        ))}
                      </div>
                    </div>

                    <div className="h-[110px] bg-white rounded-lg border border-slate-200/60 p-3 flex flex-col justify-between shadow-sm hover:shadow-md transition-shadow">
                      <div className="flex items-center gap-2">
                        <Icon name="activity" width={13} height={13} className="text-amber-500" />
                        <span className="text-[11px] font-bold text-slate-700">{t("micro.cardiology.riskFactors")}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xl font-black text-slate-900">12<span className="text-xs text-slate-400">%</span></span>
                        <span className="text-[9px] font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-md border border-amber-200/60 truncate max-w-[50%] text-center">{t("micro.cardiology.intermediate")}</span>
                      </div>
                      <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden">
                        <motion.div className="h-full bg-amber-400 rounded-full" initial={{ width: 0 }} animate={{ width: "30%" }} transition={{ duration: 1 }} />
                      </div>
                      <div className="flex justify-between text-[8px] text-slate-400 font-bold">
                        <span>{t("micro.cardiology.lowRisk")}</span><span>{t("micro.cardiology.highRisk")}</span>
                      </div>
                    </div>
                  </>
                )}

                {activeSpecialty === "orthopaedics" && (
                  <>
                    <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">{t("micro.orthopaedics.postOp")}</h4>

                    <div className="h-[145px] bg-white rounded-lg border border-slate-200/60 p-3 flex flex-col justify-between shadow-sm hover:shadow-md transition-shadow">
                      <div className="flex items-center gap-2">
                        <Icon name="bone" width={13} height={13} className="text-blue-500" />
                        <span className="text-[11px] font-bold text-slate-700">{t("micro.orthopaedics.traumaImaging")}</span>
                      </div>
                      <div className="h-12 bg-slate-50 rounded-lg flex items-center justify-center border border-slate-200/80 gap-2 cursor-pointer hover:bg-slate-100/70 transition-colors px-1">
                        <Icon name="file" width={14} height={14} className="text-slate-400 shrink-0" />
                        <span className="text-[10px] font-bold text-slate-600 truncate">{t("micro.orthopaedics.view")}</span>
                      </div>
                      <div className="flex gap-1 flex-wrap">
                        {[t("micro.orthopaedics.noFracture"), t("micro.orthopaedics.mildEffusion"), t("micro.orthopaedics.aclIntact")].map((tag, i) => (
                          <motion.span key={i} initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.1 }}
                            className="text-[8px] font-bold text-slate-600 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded-md max-w-[100px] truncate">{tag}</motion.span>
                        ))}
                      </div>
                    </div>

                    <div className="h-[110px] bg-white rounded-lg border border-slate-200/60 p-3 flex flex-col justify-between shadow-sm hover:shadow-md transition-shadow">
                      <div className="flex items-center gap-2">
                        <Icon name="user" width={13} height={13} className="text-emerald-500" />
                        <span className="text-[11px] font-bold text-slate-700">{t("micro.orthopaedics.rehab")}</span>
                      </div>
                      <div className="flex justify-between text-[9px] text-slate-500">
                        <span className="truncate w-[75%]">{t("micro.orthopaedics.targetRom")}</span>
                        <span className="font-bold text-emerald-600 shrink-0">65%</span>
                      </div>
                      <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                        <motion.div className="h-full bg-emerald-500 rounded-full" initial={{ width: 0 }} animate={{ width: "65%" }} transition={{ delay: 0.5, duration: 1 }} />
                      </div>
                      <div className="text-[8px] text-slate-400 font-bold truncate">{t("micro.orthopaedics.day14")}</div>
                    </div>
                  </>
                )}

                {activeSpecialty === "oncology" && (
                  <>
                    <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">{t("micro.oncology.protocol")}</h4>

                    <div className="h-[145px] bg-white rounded-lg border border-slate-200/60 p-3 flex flex-col justify-between shadow-sm hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Icon name="file" width={13} height={13} className="text-purple-500" />
                          <span className="text-[11px] font-bold text-slate-700">{t("micro.oncology.cycleTracking")}</span>
                        </div>
                        <div className="text-[9px] font-black text-purple-700 bg-purple-50 border border-purple-200/60 rounded px-1.5 py-0.5 truncate ml-1">{t("micro.oncology.folfox")}</div>
                      </div>
                      <p className="text-[9px] font-medium text-slate-500 leading-tight">{t("micro.oncology.cycleProgress")}</p>
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5, 6].map((v) => (
                          <motion.div key={v} initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: v * 0.07 }}
                            className={cn("w-3.5 h-3.5 rounded-full border flex items-center justify-center shrink-0", v <= 3 ? "bg-purple-500 border-purple-600" : "bg-slate-100 border-slate-300")}>
                            {v <= 3 && <div className="w-1 h-1 bg-white rounded-full" />}
                          </motion.div>
                        ))}
                        <span className="text-[8px] font-bold text-slate-400 ml-1 truncate">{t("micro.oncology.done3of6")}</span>
                      </div>
                    </div>

                    <div className="h-[110px] bg-white rounded-lg border border-slate-200/60 p-3 flex flex-col justify-between shadow-sm hover:shadow-md transition-shadow">
                      <div className="flex items-center gap-2">
                        <Icon name="idCard" width={13} height={13} className="text-rose-500" />
                        <span className="text-[11px] font-bold text-slate-700">{t("micro.oncology.adverseEvents")}</span>
                      </div>
                      <div className="flex flex-col gap-1">
                        {[
                          { name: t("contextualAnalysis.oncology.fatigue"), level: t("contextualAnalysis.oncology.mild"), color: "text-amber-600 bg-amber-50 border-amber-100" },
                          { name: t("contextualAnalysis.oncology.nausea"), level: t("contextualAnalysis.oncology.none"), color: "text-emerald-600 bg-emerald-50 border-emerald-100" },
                          { name: t("contextualAnalysis.oncology.neuropathy"), level: t("contextualAnalysis.oncology.mild"), color: "text-amber-600 bg-amber-50 border-amber-100" },
                        ].map((e, i) => (
                          <motion.div key={i} initial={{ opacity: 0, x: 6 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.1 }}
                            className="flex justify-between items-center bg-slate-50 border border-slate-100 rounded px-2 py-0.5 text-[9px]">
                            <span className="text-slate-600 font-medium truncate w-[60%]">{e.name}</span>
                            <span className={cn("font-bold px-1 py-0.2 rounded border text-[8px] truncate", e.color)}>{e.level}</span>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {activeSpecialty === "paediatrics" && (
                  <>
                    <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">{t("micro.paediatrics.pedCare")}</h4>

                    <div className="h-[145px] bg-white rounded-lg border border-slate-200/60 p-3 flex flex-col justify-between shadow-sm hover:shadow-md transition-shadow">
                      <div className="flex items-center gap-2">
                        <Icon name="pill" width={13} height={13} className="text-amber-500" />
                        <span className="text-[11px] font-bold text-slate-700">{t("micro.paediatrics.weightDose")}</span>
                      </div>
                      <div className="flex flex-col gap-1.5">
                        {[
                          { name: t("micro.paediatrics.amoxicillin"), dose: t("micro.paediatrics.amoxVal"), note: t("micro.paediatrics.amoxNote") },
                          { name: t("micro.paediatrics.paracetamol"), dose: t("micro.paediatrics.paraVal"), note: t("micro.paediatrics.paraNote") },
                        ].map((med, i) => (
                          <motion.div key={i} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
                            className="flex items-center justify-between bg-amber-50/40 border border-amber-100/50 rounded-lg px-2 py-1">
                            <span className="text-[10px] font-semibold text-slate-700 truncate w-[55%]">{med.name}</span>
                            <span className="text-[8px] text-amber-700 font-bold shrink-0">{med.dose}</span>
                          </motion.div>
                        ))}
                      </div>
                    </div>

                    <div className="h-[110px] bg-white rounded-lg border border-slate-200/60 p-3 flex flex-col justify-between shadow-sm hover:shadow-md transition-shadow">
                      <div className="flex items-center gap-2">
                        <Icon name="calendar" width={13} height={13} className="text-sky-500" />
                        <span className="text-[11px] font-bold text-slate-700">{t("micro.paediatrics.nextVaccine")}</span>
                      </div>
                      <div className="flex justify-between text-[9px] text-slate-500">
                        <span className="truncate w-[65%]">{t("micro.paediatrics.dtpBooster")}</span>
                        <span className="font-bold text-sky-600 shrink-0">{t("micro.paediatrics.due30m")}</span>
                      </div>
                      <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                        <motion.div className="h-full bg-sky-500 rounded-full" initial={{ width: 0 }} animate={{ width: "95%" }} transition={{ delay: 0.3, duration: 1 }} />
                      </div>
                      <div className="text-[8px] text-slate-400 font-bold truncate">{t("micro.paediatrics.immSchedule")}</div>
                    </div>
                  </>
                )}

                {activeSpecialty === "ophthalmology" && (
                  <>
                    <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">{t("micro.ophthalmology.ophCare")}</h4>

                    <div className="h-[145px] bg-white rounded-lg border border-slate-200/60 p-3 flex flex-col justify-between shadow-sm hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Icon name="doc" width={13} height={13} className="text-cyan-600" />
                          <span className="text-[11px] font-bold text-slate-700">{t("micro.ophthalmology.lensRx")}</span>
                        </div>
                      </div>
                      <div className="p-2 rounded-lg bg-cyan-50/50 border border-cyan-100 text-[9px] flex flex-col gap-1">
                        <div className="flex justify-between font-bold text-slate-700">
                          <span className="truncate">{t("micro.ophthalmology.rxVal1")}</span>
                          <span className="truncate ml-1">{t("micro.ophthalmology.rxVal2")}</span>
                        </div>
                        <span className="text-[8px] font-medium text-slate-500 truncate">{t("micro.ophthalmology.rxNote")}</span>
                      </div>
                      <span className="text-[8px] font-bold text-cyan-700 bg-cyan-50 border border-cyan-100 px-2 py-0.5 rounded text-center truncate">{t("micro.ophthalmology.formulary")}</span>
                    </div>

                    <div className="h-[110px] bg-white rounded-lg border border-slate-200/60 p-3 flex flex-col justify-between shadow-sm hover:shadow-md transition-shadow">
                      <div className="flex items-center gap-2">
                        <Icon name="file" width={13} height={13} className="text-purple-500" />
                        <span className="text-[11px] font-bold text-slate-700">{t("micro.ophthalmology.octScan")}</span>
                      </div>
                      <div className="flex items-center justify-between text-[9px] bg-slate-50 border border-slate-100 rounded px-2 py-1">
                        <span className="font-semibold text-slate-700 truncate w-[65%]">{t("micro.ophthalmology.macularOct")}</span>
                        <span className="text-[8px] font-bold text-emerald-600 shrink-0">{t("micro.ophthalmology.attached")}</span>
                      </div>
                      <div className="text-[8px] text-slate-400 font-bold truncate">{t("micro.ophthalmology.foveal")}</div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
